/*
 * @author HOPS
 * @copyright Heritage Operations Processing Limited
 */

import {useCallback, useEffect, useState} from "react";
import {Checkbox} from "Components/Checkbox.js";
import {DateTimePicker} from "Components/DateTimePicker.js";
import {FlexContainer} from "Components/FlexContainer.js";
import {KeycafeKeyPicker} from "Bookables/Keycafe/KeycafeKeyPicker.js";
import Link from "Components/Link.js";
import {TextField} from "Components/TextField.js";
import Navigator from "App/Navigator.js";
import {Typography} from "Components/Typography.js";
import {BookablesUnitPicker} from "Bookables/BookablesUnitPicker.js";
import {BookablesUnitTypePicker} from "Bookables/BookablesUnitTypePicker.js";
import withAuth from "Hoc/withAuth.js";
import withSnack from "Hoc/withSnack.js";
import {withRouter} from "react-router-dom";
import {BookablesService} from "@hps/hops-sdk-js";
import {EditForm} from "ComponentsE/form/EditForm";
import {SaveCancel} from "ComponentsE/form/SaveCancel";
import {EditFormTableList} from "Components/EditFormTableList";
import {useSnackbarService} from "Services/useSnackbarService";
import {useFetch} from "Hooks/useFetch";
import {ErrorRetry} from "ComponentsE/error/ErrorRetry";
import {Breadcrumbs} from "ComponentsE/navigation/Breadcrumbs";
import {PageHeader} from "ComponentsE/containers/PageHeader";
import {PageTitle} from "ComponentsE/text/PageTitle";

const BookablesUnitFormView = props => {
	const {snack} = useSnackbarService();

	const [data, setData] = useState({});

	const [submitting, setSubmitting] = useState(false);

	const unitId = (parseInt(props.match.params.id) || null);

	const state = useFetch({
		fn() {
			if (!unitId) {
				return new Promise(resolve => {
					resolve({
						Name: null,
						NameShort: null,
						Type: null,
						Parent: null,
						DisplayOrder: 0,
						CheckInTime: null,
						CheckOutTime: null,
						KeycafeKeyId: null,
						Archived: null,
						ShowOnDailySheet: false
					});
				});
			}
			else {
				return BookablesService.getUnit(unitId);
			}
		},
		dep: [unitId]
	});

	useEffect(() => {
		setData((state.data || {}));
	}, [state.data]);

	const handleChange = useCallback(
		(value, name) => setData({...data, [name]: value}),
		[data]
	);

	/**
	 * Reset the check-in/check-out time values
	 */
	const handleCheckInOutTimesReset = useCallback(
		() => setData({...data, CheckInTime: null, CheckOutTime: null}),
		[data]
	);

	const handleSubmit = async () => {
		const submittedUnitId = unitId;

		const submission = {
			...data,
			Type: data.Type?.Id,
			Parent: (data.Parent?.Id || null),
			CheckInTime: (data.CheckInTime ? ((data.CheckInTime.length === 5) ? `${data.CheckInTime}:00` : data.CheckInTime) : null),
			CheckOutTime: (data.CheckOutTime ? ((data.CheckOutTime.length === 5) ? `${data.CheckOutTime}:00` : data.CheckOutTime) : null),
			ShowOnDailySheet: data.ShowOnDailySheet,
			NameShort: data.NameShort
		};

		setSubmitting(true);

		const promise = (
			!unitId ?
				BookablesService.createUnit(submission) :
				BookablesService.updateUnit(unitId, submission)
		);

		try {
			await promise;
			if (unitId === submittedUnitId) {
				Navigator.navigate("/rb/admin/units");
				snack("Saved.", "success");
			}
		}
		catch (e) {
			if (unitId === submittedUnitId) {
				props.snack(e);
				setSubmitting(false);
			}
		}
	};

	const tableData = [
		{
			label: "Name",
			content: (
				<TextField
					autoFocus={true}
					disabled={submitting}
					placeholder="Name"
					maxLength={250}
					name="Name"
					onChange={handleChange}
					required={true}
					value={data.Name} />
			)
		},
		{
			label: "Short Name",
			content: (
				<TextField
					disabled={submitting}
					placeholder="Short Name"
					maxLength={8}
					name="NameShort"
					onChange={handleChange}
					required={true}
					value={data.NameShort} />
			)
		},
		{
			label: "Type",
			content: (
				<BookablesUnitTypePicker
					disabled={submitting}
					placeholder="Type"
					name="Type"
					onChange={handleChange}
					required={true}
					value={data.Type} />
			)
		},
		{
			label: "Parent",
			content: (
				<BookablesUnitPicker
					showNullOption={true}
					disabled={submitting}
					isOptionDisabled={(data.Id ? unit => !BookablesService.canUnitBeParentedTo(data, unit) : undefined)}
					name="Parent"
					onChange={handleChange}
					value={data.Parent} />
			)
		},
		{
			label: "Order",
			content: (
				<FlexContainer alignItems="flex-start">
					<TextField
						disabled={submitting}
						placeholder="Order"
						name="DisplayOrder"
						type="number"
						onChange={handleChange}
						required={true}
						value={data.DisplayOrder} />
				</FlexContainer>
			)
		},
		{
			label: "Fixed Check-In/Check-Out Times (optional)",
			content: (
				<FlexContainer
					alignItems="center"
					columnar={true}
					wrap={true}>
					<DateTimePicker
						disabled={submitting}
						label="Check-In Time"
						fullWidth={true}
						name="CheckInTime"
						onChange={handleChange}
						timeOnly={true}
						value={data.CheckInTime} />

					<Typography>to</Typography>

					<DateTimePicker
						disabled={submitting}
						label="Check-Out Time"
						fullWidth={true}
						name="CheckOutTime"
						onChange={handleChange}
						timeOnly={true}
						value={data.CheckOutTime} />

					<Link
						disabled={submitting}
						label="Reset"
						onClick={handleCheckInOutTimesReset} />
				</FlexContainer>
			)
		},
		{
			label: "Keycafe Key",
			content: (
				<KeycafeKeyPicker
					name="KeycafeKeyId"
					disabled={submitting}
					onChange={handleChange}
					value={data.KeycafeKeyId} />
			),
			hidden: !props.org.KeycafeEnabled
		},
		{
			label: "Show on Daily Sheet",
			content: (
				<Checkbox
					checked={data.ShowOnDailySheet}
					disabled={submitting}
					label=""
					name="ShowOnDailySheet"
					onChange={handleChange}
					value={!data.ShowOnDailySheet} />
			)
		},
		{
			label: "Archived",
			content: (
				<Checkbox
					checked={data.Archived}
					disabled={submitting}
					label="Archived units are unavailable for booking."
					name="Archived"
					onChange={handleChange}
					value={!data.Archived} />
			),
			hidden: !unitId
		}
	];

	let view;
	if (state.loading) {
		view = <Typography>Loading...</Typography>;
	}
	else if (state.error) {
		view = <ErrorRetry error={state.error} subject="Unit" onRetry={state.fetch} />;
	}
	else {
		view = (
			<>
				<PageHeader>
					<PageTitle>Bookable Unit</PageTitle>
				</PageHeader>

				<EditForm onSubmit={handleSubmit}>
					<fieldset>
						<legend>{!unitId ? "New Bookable Unit" : state.data?.Name}</legend>

						<EditFormTableList data={tableData}/>
					</fieldset>

					<fieldset className="submit_buttons">
						<SaveCancel
							submitting={submitting}
							onCancel={() => Navigator.navigate("/rb/admin/units")} />
					</fieldset>
				</EditForm>
			</>
		);
	}

	return (
		<FlexContainer>
			<Breadcrumbs>
				<Link uri="/rb/admin/units">
				All Units
				</Link>
			</Breadcrumbs>

			{view}
		</FlexContainer>
	);
};

export default withAuth(withRouter(withSnack(BookablesUnitFormView)));
